import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { setApiErrorHandler } from '../services/api';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface AppError {
  id: string;
  message: string;
  severity: ErrorSeverity;
  retryFn?: () => void;
}

interface ErrorContextType {
  /** Current error displayed in toast (null = hidden) */
  error: AppError | null;
  /** Whether the browser is offline */
  isOffline: boolean;
  /** Show a toast error with optional retry callback */
  showError: (message: string, severity?: ErrorSeverity, retryFn?: () => void) => void;
  /** Dismiss the current toast */
  dismissError: () => void;
}

export const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
}

let nextId = 0;

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<AppError | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const showError = useCallback(
    (message: string, severity: ErrorSeverity = 'error', retryFn?: () => void) => {
      setError({ id: String(++nextId), message, severity, retryFn });
    },
    []
  );

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  // Keep a ref to showError so the API interceptor always has the latest callback
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // Wire up the Axios interceptor → global toast
  useEffect(() => {
    setApiErrorHandler((message, retryFn) => showErrorRef.current(message, 'error', retryFn));
    return () => setApiErrorHandler(null);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ErrorContext.Provider value={{ error, isOffline, showError, dismissError }}>
      {children}
    </ErrorContext.Provider>
  );
}
